import type { KeyboardEvent } from 'react';
import { useEffect, useState } from 'react';

import type { A11y, AccordionTheme, Classnames, StringOrNode } from '@snowit/icetypes';

import useA11y from '@snowit/hooks/useA11y';
import { Keyboard } from '@snowit/iceconst';
import testId from '@snowit/utils/testId';

import { Typo } from '../';
import Icon from '../Icon';

import cx from 'classnames';
import s from './Accordion.module.scss';

export interface IAccordion {
  theme?: AccordionTheme;
  header: StringOrNode;
  body?: StringOrNode;
  error?: boolean;
  errorMsg?: string;
  disabled?: boolean;
  disabledClose?: boolean;
  hideArrow?: boolean;
  mounted?: boolean;
  onMount?: () => void;
  onUnmount?: () => void;
  id?: string;
  classNames?: Classnames<'wrapper' | 'accordion' | 'header' | 'arrow' | 'body'>;
  bodyA11y?: A11y;
  /** Jest/Cypress test string */
  test?: string;
}

const Accordion = (props: IAccordion) => {
  const {
    theme = 'default',
    header,
    body,
    error,
    errorMsg,
    disabled,
    disabledClose,
    hideArrow,
    mounted,
    onMount,
    onUnmount,
    id,
    classNames,
    bodyA11y,
    test,
  } = props;
  const a11yH = useA11y();
  const [showBody, setShowBody] = useState(mounted);

  const headerId = a11yH.makeId('header');
  const bodyId = a11yH.makeId('body');

  useEffect(() => {
    setShowBody(mounted);
  }, [mounted]);

  const toggleContent = () => {
    if (!body) return;
    const willMount = !showBody;
    if (willMount) {
      typeof onMount === 'function' && onMount();
    } else {
      typeof onUnmount === 'function' && onUnmount();
    }
    setShowBody(willMount);
  };

  const handleClick = () => {
    if (disabledClose || !body) return;
    toggleContent();
  };

  const handleKeydown = (event: KeyboardEvent<HTMLHtmlElement>) => {
    if (event.key !== Keyboard.ENTER && event.key !== Keyboard.ESC) return;
    handleClick();
  };

  const renderArrow = () => {
    if (hideArrow) return null;
    return (
      <Icon
        className={cx(s.arrow, classNames?.arrow)}
        name={showBody ? 'arrow_angle_up' : 'arrow_angle_down'}
      />
    );
  };

  const renderHeader = () => {
    const headerContent = typeof header === 'function' ? header() : header;

    return (
      <header
        id={headerId}
        className={cx(s.header, classNames?.header)}
        onClick={handleClick}
        onKeyDown={handleKeydown}
        role={disabledClose ? 'presentation' : 'button'}
        aria-controls={bodyId}
        aria-expanded={showBody}
        tabIndex={disabledClose ? -1 : 0}
        {...testId(test, 'accordion_header')}
      >
        {headerContent}
        {renderArrow()}
      </header>
    );
  };

  const renderBody = () => {
    const bodyContent = typeof body === 'function' ? body() : body;
    return (
      <div
        id={bodyId}
        className={cx(s.body, classNames?.body)}
        role={bodyA11y?.role || 'region'}
        aria-labelledby={headerId}
        hidden={!showBody}
        {...testId(test, 'accordion_content')}
      >
        {bodyContent}
      </div>
    );
  };

  const renderError = () => {
    if (!error || !errorMsg) return null;
    return (
      <Typo
        className={s.errorMsg}
        text={errorMsg}
        tag={'p'}
        size={'md'}
        color={'error'}
        {...testId(test, 'accordion_error')}
      />
    );
  };

  return (
    <div
      className={cx(s.wrapper, classNames?.wrapper, {
        [s[theme]]: true,
        [s.noBody]: !body,
        [s.showBody]: showBody,
        [s.error]: error,
        [s.disabled]: disabled,
        [s.disabledClose]: disabledClose,
        [s.hideArrow]: hideArrow,
      })}
    >
      <div
        className={cx(s.accordion, classNames?.accordion)}
        id={id}
        {...testId(`accordion_container_${test}${disabled ? '_disabled' : ''}`)}
      >
        {renderHeader()}
        {renderBody()}
      </div>
      {renderError()}
    </div>
  );
};

export default Accordion;
